import { ActivityPanel } from "@/components/diagnostics/ActivityPanel";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useEscapeBack } from "@/hooks/use-escape-back";
import { defaultPage, diagnosticsClient, shortId } from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	AppWindow,
	CheckCircle2,
	ChevronLeft,
	ChevronRight,
	Circle,
	Eye,
	EyeOff,
	FileText,
	Keyboard,
	Monitor,
	MousePointer2,
	MousePointerClick,
	Pause,
	Play,
	SkipBack,
	SkipForward,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/diagnostics/session/$id")({
	component: SessionViewerPage,
});

/** Get readable label for a key event. Prefers `char` field (server-side layout-aware),
 *  falls back to raw code. */
function keyLabel(ev: { char?: string; code: string }): string {
	if (ev.char) return ev.char;
	return ev.code;
}

// ── Types ──
interface InputEvent {
	timestamp_millis: number;
	event_type: string;
	code: string;
	char?: string;
	x?: number;
	y?: number;
	modifiers?: string[];
}
interface InputBatch {
	events: InputEvent[];
	capture_window_started_at_millis?: number;
	capture_window_ended_at_millis?: number;
	aligned_frames?: {
		width: number;
		height: number;
		display_geometry?: { width_points: number; height_points: number };
	}[];
}
interface ContextBatch {
	windows: { app_name: string; title: string; is_focused: boolean }[];
}

function useSegmentEvents(inputUrl?: string, contextUrl?: string) {
	const { data: inputData } = useQuery<InputBatch>({
		queryKey: ["seg-in", inputUrl],
		queryFn: () => fetch(inputUrl!).then((r) => r.json()),
		enabled: !!inputUrl,
		staleTime: Number.POSITIVE_INFINITY,
	});
	const { data: contextData } = useQuery<ContextBatch>({
		queryKey: ["seg-ctx", contextUrl],
		queryFn: () => fetch(contextUrl!).then((r) => r.json()),
		enabled: !!contextUrl,
		staleTime: Number.POSITIVE_INFINITY,
	});
	return { inputData, contextData };
}

// ── Live overlay — synced with video playback, accounting for letterboxing ──

/** Compute object-contain rect from media's intrinsic size + container size */
function getMediaRect(
	mediaEl: HTMLVideoElement | HTMLImageElement | null,
	containerEl: HTMLElement | null,
) {
	if (!containerEl)
		return { renderedW: 0, renderedH: 0, offsetX: 0, offsetY: 0 };
	const cw = containerEl.clientWidth;
	const ch = containerEl.clientHeight;

	// Get intrinsic media size
	let natW = 0;
	let natH = 0;
	if (mediaEl instanceof HTMLVideoElement) {
		natW = mediaEl.videoWidth;
		natH = mediaEl.videoHeight;
	} else if (mediaEl instanceof HTMLImageElement) {
		natW = mediaEl.naturalWidth;
		natH = mediaEl.naturalHeight;
	}
	if (natW === 0 || natH === 0)
		return { renderedW: cw, renderedH: ch, offsetX: 0, offsetY: 0 };

	// object-contain: fit within container preserving aspect ratio
	const scale = Math.min(cw / natW, ch / natH);
	const renderedW = natW * scale;
	const renderedH = natH * scale;
	const offsetX = (cw - renderedW) / 2;
	const offsetY = (ch - renderedH) / 2;
	return { renderedW, renderedH, offsetX, offsetY };
}

function EventOverlay({
	inputData,
	show,
	w,
	h,
	videoRef,
	imgRef,
	containerRef,
	segmentKey,
}: {
	inputData?: InputBatch;
	show: boolean;
	w: number;
	h: number;
	videoRef: React.RefObject<HTMLVideoElement | null>;
	imgRef: React.RefObject<HTMLImageElement | null>;
	containerRef: React.RefObject<HTMLDivElement | null>;
	segmentKey: string;
}) {
	const [progress, setProgress] = useState(0); // 0–1
	const [mediaRect, setMediaRect] = useState({
		renderedW: 0,
		renderedH: 0,
		offsetX: 0,
		offsetY: 0,
	});

	// Reset progress when segment changes
	useEffect(() => {
		setProgress(0);
	}, [segmentKey]);

	// Poll video currentTime + media rect for sync
	useEffect(() => {
		if (!show || !inputData?.events?.length) return;
		let raf: number;
		const tick = () => {
			const vid = videoRef.current;
			if (vid && vid.duration > 0) {
				setProgress(vid.currentTime / vid.duration);
			} else {
				setProgress(1);
			}
			// Update media rect every frame for accuracy
			const el = videoRef.current ?? imgRef.current;
			const rect = getMediaRect(el, containerRef.current);
			if (rect.renderedW > 0) setMediaRect(rect);
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [show, inputData, videoRef, imgRef, containerRef, segmentKey]);

	const { renderedW, renderedH, offsetX, offsetY } = mediaRect;
	if (!show || !inputData?.events?.length || renderedW === 0) return null;
	const events = inputData.events;
	const frame = inputData.aligned_frames?.[0];

	const displayW = frame?.display_geometry?.width_points || 1728;
	const displayH = frame?.display_geometry?.height_points || 1117;

	const mapX = (px: number) => offsetX + (px / displayW) * renderedW;
	const mapY = (py: number) => offsetY + (py / displayH) * renderedH;

	const withPos = events.filter((e) => e.x != null);
	if (withPos.length === 0) return null;

	// Use first/last event timestamps for the time range
	// (capture_window_ended_at_millis is unreliable)
	const t0 = withPos[0].timestamp_millis;
	const t1 = withPos.at(-1)!.timestamp_millis;
	const range = t1 - t0 || 1;
	const cutoff = t0 + range * progress;

	const visible = withPos.filter((e) => e.timestamp_millis <= cutoff);
	const lastMove = visible.findLast(
		(e: InputEvent) => e.event_type === "mouse_move",
	);

	// Recent trail
	const recentMoves = visible.filter((e) => e.event_type === "mouse_move");
	const trail = recentMoves.slice(Math.max(0, recentMoves.length - 30));

	// Click ripples (visible for ~500ms worth of progress)
	const clickWindow = Math.max(range * 0.04, 300);
	const recentClicks = visible.filter(
		(e: InputEvent) =>
			e.event_type === "mouse_down" &&
			e.timestamp_millis > cutoff - clickWindow,
	);

	return (
		<svg
			className="absolute inset-0 pointer-events-none"
			width={w}
			height={h}
			viewBox={`0 0 ${w} ${h}`}
		>
			{/* Trail */}
			{trail.length > 1 && (
				<polyline
					points={trail
						.map((e) => `${mapX(e.x ?? 0)},${mapY(e.y ?? 0)}`)
						.join(" ")}
					fill="none"
					stroke="rgba(59,130,246,0.3)"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			)}

			{/* Click ripples */}
			{recentClicks.map((e, i) => {
				const age = (cutoff - e.timestamp_millis) / clickWindow;
				const opacity = Math.max(0.15, 1 - age);
				const r = 5 + age * 15;
				return (
					<g
						key={i}
						transform={`translate(${mapX(e.x ?? 0)},${mapY(e.y ?? 0)})`}
					>
						<circle
							r={r}
							fill="none"
							stroke={`rgba(239,68,68,${opacity * 0.7})`}
							strokeWidth="2"
						/>
						<circle r="3" fill={`rgba(239,68,68,${opacity})`} />
					</g>
				);
			})}

			{/* Cursor */}
			{lastMove && (
				<g
					transform={`translate(${mapX(lastMove.x ?? 0)},${mapY(lastMove.y ?? 0)})`}
				>
					<polygon
						points="0,0 0,16 4,12 8,18 10,17 6,11 12,11"
						fill="white"
						stroke="black"
						strokeWidth="1"
						transform="scale(1.1)"
					/>
				</g>
			)}
		</svg>
	);
}

// ── Events sidebar — individual events, auto-scrolling with playback ──

interface FlatEvent {
	segIdx: number;
	type: "app_switch" | "window_change" | "click" | "key" | "drag";
	app: string;
	window: string;
	label: string;
	detail?: string;
	color?: string;
	ts: number; // timestamp for ordering within segment
}

function useFlatEvents(
	segments: {
		appName?: string;
		windowTitle?: string;
		inputEventsUrl?: string;
	}[],
) {
	const urls = segments
		.map((s) => s.inputEventsUrl)
		.filter(Boolean) as string[];
	const batches = useQuery<Record<number, InputBatch>>({
		queryKey: ["all-input-batches", urls.length],
		queryFn: async () => {
			const result: Record<number, InputBatch> = {};
			await Promise.all(
				segments.map(async (seg, i) => {
					if (!seg.inputEventsUrl) return;
					try {
						result[i] = await fetch(seg.inputEventsUrl).then((r) => r.json());
					} catch {}
				}),
			);
			return result;
		},
		enabled: urls.length > 0,
		staleTime: Number.POSITIVE_INFINITY,
	});

	return useMemo(() => {
		const flat: FlatEvent[] = [];
		let prevApp = "";
		let prevWin = "";

		for (let i = 0; i < segments.length; i++) {
			const seg = segments[i];
			const app = seg.appName || "";
			const win = seg.windowTitle || "";

			// App switch event
			if (app && app !== prevApp) {
				flat.push({
					segIdx: i,
					type: "app_switch",
					app,
					window: win,
					label: app,
					detail: win,
					ts: 0,
				});
				prevApp = app;
				prevWin = win;
			} else if (win && win !== prevWin) {
				flat.push({
					segIdx: i,
					type: "window_change",
					app,
					window: win,
					label: win,
					ts: 0,
				});
				prevWin = win;
			}

			// Input events (skip mouse_move — too noisy)
			const batch = batches.data?.[i];
			if (batch?.events) {
				for (const ev of batch.events) {
					if (ev.event_type === "mouse_down") {
						flat.push({
							segIdx: i,
							type: "click",
							app,
							window: win,
							label:
								ev.code === "mouse_left"
									? "Клик"
									: ev.code === "mouse_right"
										? "Правый клик"
										: ev.code,
							detail: `(${Math.round(ev.x ?? 0)}, ${Math.round(ev.y ?? 0)})`,
							ts: ev.timestamp_millis,
						});
					} else if (
						ev.event_type === "key_down" &&
						ev.code !== "pointer_move"
					) {
						const ch = keyLabel(ev);
						flat.push({
							segIdx: i,
							type: "key",
							app,
							window: win,
							label: ch,
							ts: ev.timestamp_millis,
						});
					} else if (ev.event_type === "mouse_drag") {
						flat.push({
							segIdx: i,
							type: "drag",
							app,
							window: win,
							label: "Перетаскивание",
							ts: ev.timestamp_millis,
						});
					}
				}
			}
		}

		// Group consecutive keystrokes into words
		const merged: FlatEvent[] = [];
		for (const ev of flat) {
			const prev = merged.at(-1);
			if (
				ev.type === "key" &&
				prev?.type === "key" &&
				prev.segIdx === ev.segIdx
			) {
				prev.label += " " + ev.label;
				prev.ts = ev.ts;
			} else {
				merged.push({ ...ev });
			}
		}
		return merged;
	}, [segments, batches.data]);
}

function EventsSidebar({
	segments,
	currentIndex,
	onSelect,
	appColors,
	videoRef: _videoRef,
}: {
	segments: {
		appName?: string;
		windowTitle?: string;
		inputEventsUrl?: string;
	}[];
	currentIndex: number;
	onSelect: (i: number) => void;
	appColors: Record<string, string>;
	videoRef: React.RefObject<HTMLVideoElement | null>;
}) {
	const events = useFlatEvents(segments);
	const scrollRef = useRef<HTMLDivElement>(null);
	const [activeEventIdx, setActiveEventIdx] = useState(0);

	// Find the last event for the current segment
	useEffect(() => {
		const lastIdx = events.findLastIndex(
			(e: FlatEvent) => e.segIdx <= currentIndex,
		);
		if (lastIdx >= 0) setActiveEventIdx(lastIdx);
	}, [currentIndex, events]);

	// Auto-scroll
	useEffect(() => {
		const el = scrollRef.current?.querySelector(
			`[data-event-idx="${activeEventIdx}"]`,
		);
		el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
	}, [activeEventIdx]);

	return (
		<div className="w-72 border-l bg-background flex flex-col shrink-0 overflow-hidden">
			<div className="px-3 py-1.5 border-b flex items-center justify-between">
				<span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
					Хронология
				</span>
				<span className="text-[10px] text-muted-foreground tabular-nums">
					{events.length} событий
				</span>
			</div>
			<div ref={scrollRef} className="flex-1 overflow-y-auto">
				{events.map((ev, i) => {
					const isPast = ev.segIdx < currentIndex;
					const isCurrent = ev.segIdx === currentIndex;
					const isActive = i === activeEventIdx;
					const color = ev.app ? appColors[ev.app] : undefined;

					if (ev.type === "app_switch") {
						return (
							<div
								key={i}
								data-event-idx={i}
								className="sticky top-0 z-10 flex items-center gap-2 px-3 py-1.5 bg-muted/90 backdrop-blur-sm border-b cursor-pointer hover:bg-muted"
								onClick={() => onSelect(ev.segIdx)}
							>
								<span
									className="w-2.5 h-2.5 rounded-full shrink-0"
									style={{ backgroundColor: color }}
								/>
								<span className="text-[11px] font-semibold truncate">
									{ev.app}
								</span>
								{ev.detail && (
									<span className="text-[10px] text-muted-foreground truncate ml-auto max-w-[120px]">
										{ev.detail}
									</span>
								)}
							</div>
						);
					}

					if (ev.type === "window_change") {
						return (
							<div
								key={i}
								data-event-idx={i}
								className="flex items-center gap-2 px-3 py-1 border-b border-dashed cursor-pointer hover:bg-muted/50"
								onClick={() => onSelect(ev.segIdx)}
							>
								<AppWindow className="h-3 w-3 text-muted-foreground shrink-0" />
								<span className="text-[10px] text-muted-foreground truncate">
									{ev.label}
								</span>
							</div>
						);
					}

					return (
						<button
							key={i}
							data-event-idx={i}
							onClick={() => onSelect(ev.segIdx)}
							className={`w-full text-left px-3 py-0.5 flex items-center gap-2 text-[11px] transition-colors border-l-2 ${
								isActive
									? "bg-primary/10 border-l-primary"
									: isCurrent
										? "bg-primary/5 border-l-primary/30"
										: isPast
											? "border-l-transparent opacity-50"
											: "border-l-transparent opacity-70 hover:opacity-100 hover:bg-muted/30"
							}`}
						>
							{ev.type === "click" && (
								<MousePointerClick className="h-3 w-3 text-red-500 shrink-0" />
							)}
							{ev.type === "key" && (
								<Keyboard className="h-3 w-3 text-blue-500 shrink-0" />
							)}
							{ev.type === "drag" && (
								<MousePointer2 className="h-3 w-3 text-orange-500 shrink-0" />
							)}

							<span
								className={`truncate ${ev.type === "key" ? "font-mono text-[10px]" : ""}`}
							>
								{ev.label}
							</span>

							{ev.detail && (
								<span className="text-[9px] text-muted-foreground ml-auto shrink-0">
									{ev.detail}
								</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
}

// ── Filmstrip ──
function Filmstrip({
	segments,
	currentIndex,
	onSelect,
}: {
	segments: { thumbnailUrl?: string }[];
	currentIndex: number;
	onSelect: (i: number) => void;
}) {
	const ref = useRef<HTMLDivElement>(null);
	useEffect(() => {
		(ref.current?.children[currentIndex] as HTMLElement)?.scrollIntoView({
			behavior: "smooth",
			block: "nearest",
			inline: "center",
		});
	}, [currentIndex]);
	return (
		<div
			ref={ref}
			className="flex gap-0.5 overflow-x-auto py-1 px-3"
			style={{ scrollbarWidth: "thin" }}
		>
			{segments.map((seg, i) => (
				<button
					key={i}
					onClick={() => onSelect(i)}
					className={`shrink-0 rounded overflow-hidden transition-all ${i === currentIndex ? "ring-2 ring-primary scale-105" : "opacity-40 hover:opacity-90"}`}
					style={{ width: 64, height: 40 }}
				>
					{seg.thumbnailUrl ? (
						<img
							src={seg.thumbnailUrl}
							className="w-full h-full object-cover"
							loading="lazy"
							alt=""
						/>
					) : (
						<div className="w-full h-full bg-muted flex items-center justify-center text-[8px] text-muted-foreground">
							#{i + 1}
						</div>
					)}
				</button>
			))}
		</div>
	);
}

// ── Speed ──
const SPEEDS = [0.5, 1, 1.5, 2, 4];

// ── Main ──
function SessionViewerPage() {
	const { id } = Route.useParams();
	useEscapeBack("/diagnostics/sessions");
	useBreadcrumbs([
		{ label: "Диагностика", href: "/diagnostics" },
		{ label: "Сессии", href: "/diagnostics/sessions" },
		{ label: shortId(id) },
	]);

	const [idx, setIdx] = useState(0);
	const [playing, setPlaying] = useState(true);
	const [overlay, setOverlay] = useState(true);
	const [speed, setSpeed] = useState(1);
	const [ocrOpen, setOcrOpen] = useState(false);
	const vidRef = useRef<HTMLVideoElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);
	const areaRef = useRef<HTMLDivElement>(null);
	const [areaSize, setAreaSize] = useState({ w: 1280, h: 720 });
	const [hover, setHover] = useState<number | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: ["diagnostics", "session-segments", id],
		queryFn: () =>
			diagnosticsClient.getReplaySegments({
				replayRequestId: "",
				sessionId: id,
				page: defaultPage(),
			}),
	});

	const segs = data?.segments ?? [];
	const cur = segs[idx];
	// Cast to access fields that may exist on the proto but aren't in the generated TS types yet
	const curAny = cur as unknown as Record<string, unknown> | undefined;
	const { inputData } = useSegmentEvents(
		(curAny?.inputEventsUrl as string) || undefined,
		(curAny?.contextUrl as string) || undefined,
	);

	const go = useCallback(
		(i: number) => {
			if (i >= 0 && i < segs.length) setIdx(i);
		},
		[segs.length],
	);
	useEffect(() => {
		if (vidRef.current) vidRef.current.playbackRate = speed;
	}, [speed, idx]);
	const onEnd = useCallback(() => {
		if (playing && idx < segs.length - 1) go(idx + 1);
	}, [playing, idx, segs.length, go]);

	const isVid = cur?.presignedUrl?.includes(".mp4");
	useEffect(() => {
		if (!isVid && playing && cur?.presignedUrl) {
			const t = setTimeout(() => {
				if (idx < segs.length - 1) go(idx + 1);
			}, 2000 / speed);
			return () => clearTimeout(t);
		}
	}, [isVid, playing, idx, segs.length, go, cur?.presignedUrl, speed]);

	useEffect(() => {
		const h = (e: KeyboardEvent) => {
			if (e.key === "ArrowRight") {
				e.preventDefault();
				go(idx + 1);
			} else if (e.key === "ArrowLeft") {
				e.preventDefault();
				go(idx - 1);
			} else if (e.key === " ") {
				e.preventDefault();
				setPlaying((p) => !p);
			} else if (e.key === "o") setOverlay((p) => !p);
			else if (e.key === "Home") {
				e.preventDefault();
				go(0);
			} else if (e.key === "End") {
				e.preventDefault();
				go(segs.length - 1);
			}
		};
		window.addEventListener("keydown", h);
		return () => window.removeEventListener("keydown", h);
	}, [idx, segs.length, go]);

	useEffect(() => {
		const el = areaRef.current;
		if (!el) return;
		const obs = new ResizeObserver(([e]) =>
			setAreaSize({ w: e.contentRect.width, h: e.contentRect.height }),
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, []);

	const appColors = useMemo(() => {
		const c: Record<string, string> = {};
		const p = [
			"hsl(var(--chart-1))",
			"hsl(var(--chart-2))",
			"hsl(var(--chart-3))",
			"hsl(var(--chart-4))",
			"hsl(var(--chart-5))",
		];
		let i = 0;
		for (const s of segs)
			if (s.appName && !c[s.appName]) c[s.appName] = p[i++ % p.length];
		return c;
	}, [segs]);

	if (isLoading)
		return (
			<div className="flex flex-col gap-4 p-4">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="aspect-video w-full" />
			</div>
		);

	if (segs.length === 0)
		return (
			<div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
				<Monitor className="h-16 w-16 opacity-30" />
				<p className="text-sm">Нет записанных сегментов</p>
			</div>
		);

	const hoverSeg =
		hover != null
			? (segs[hover] as unknown as Record<string, unknown> | undefined)
			: null;

	return (
		<div className="flex flex-col h-full">
			{/* Main area: viewer + sidebar */}
			<div className="flex-1 flex min-h-0">
				{/* Video/image viewer */}
				<div
					ref={areaRef}
					className="flex-1 flex items-center justify-center bg-neutral-950 relative overflow-hidden min-h-0 min-w-0"
				>
					{cur?.presignedUrl ? (
						isVid ? (
							<video
								ref={vidRef}
								key={cur.presignedUrl}
								className="max-w-full max-h-full object-contain"
								autoPlay={playing}
								muted
								onEnded={onEnd}
							>
								<source src={cur.presignedUrl} type="video/mp4" />
							</video>
						) : (
							<img
								ref={imgRef}
								key={cur.presignedUrl}
								src={cur.presignedUrl}
								alt=""
								className="max-w-full max-h-full object-contain"
								draggable={false}
							/>
						)
					) : (
						<div className="text-neutral-500 text-sm">Кадр недоступен</div>
					)}

					<EventOverlay
						inputData={inputData}
						show={overlay}
						w={areaSize.w}
						h={areaSize.h}
						videoRef={vidRef}
						imgRef={imgRef}
						containerRef={areaRef}
						segmentKey={cur?.artifactId || String(idx)}
					/>

					<button
						className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/70 hover:text-white transition disabled:opacity-15 disabled:pointer-events-none"
						disabled={idx === 0}
						onClick={() => go(idx - 1)}
					>
						<ChevronLeft className="h-5 w-5" />
					</button>
					<button
						className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/70 hover:text-white transition disabled:opacity-15 disabled:pointer-events-none"
						disabled={idx === segs.length - 1}
						onClick={() => go(idx + 1)}
					>
						<ChevronRight className="h-5 w-5" />
					</button>

					{/* Top bar */}
					<div className="absolute top-3 right-3 flex items-center gap-1.5">
						{cur?.appName && (
							<span className="bg-black/60 backdrop-blur-sm text-white text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1.5">
								<span
									className="w-2 h-2 rounded-full"
									style={{ backgroundColor: appColors[cur.appName] }}
								/>
								{cur.appName}
								{cur.windowTitle && (
									<span className="text-white/50 ml-1 truncate max-w-[180px]">
										— {cur.windowTitle}
									</span>
								)}
							</span>
						)}
						{/* Indexing health indicator */}
						<span
							className="bg-black/60 backdrop-blur-sm text-[10px] px-2 py-1 rounded-full flex items-center gap-1"
							title={curAny?.isIndexed ? "Индексировано" : "Не индексировано"}
						>
							{curAny?.isIndexed ? (
								<CheckCircle2 className="h-3 w-3 text-green-400" />
							) : (
								<Circle className="h-3 w-3 text-white/30" />
							)}
						</span>
						{/* OCR / extracted text button */}
						<button
							className="bg-black/60 backdrop-blur-sm text-white/70 hover:text-white w-7 h-7 rounded-full flex items-center justify-center transition"
							onClick={() => setOcrOpen(true)}
							title="Извлечённый текст"
						>
							<FileText className="h-3.5 w-3.5" />
						</button>
						<button
							className="bg-black/60 backdrop-blur-sm text-white/70 hover:text-white w-7 h-7 rounded-full flex items-center justify-center transition"
							onClick={() => setOverlay((p) => !p)}
							title="Наложение (O)"
						>
							{overlay ? (
								<Eye className="h-3.5 w-3.5" />
							) : (
								<EyeOff className="h-3.5 w-3.5" />
							)}
						</button>
					</div>

					<div className="absolute bottom-3 right-3 bg-black/60 text-white/80 text-[11px] px-2 py-0.5 rounded-full backdrop-blur-sm tabular-nums">
						{idx + 1} / {segs.length}
					</div>
				</div>

				{/* Activity panel — VLM-described semantic activity blocks */}
				<div className="w-72 border-l bg-background flex flex-col shrink-0 overflow-hidden">
					<ActivityPanel
						sessionId={id}
						highlightedAt={
							(
								curAny?.captureStartedAt as { toDate(): Date } | undefined
							)?.toDate() ?? null
						}
						onSelectAt={(at) => {
							const target = at.getTime();
							let bestIdx = 0;
							let bestDelta = Number.POSITIVE_INFINITY;
							for (let i = 0; i < segs.length; i++) {
								const s = segs[i] as unknown as Record<string, unknown>;
								const ts = (s?.captureStartedAt as { toDate(): Date } | undefined)
									?.toDate()
									?.getTime();
								if (ts == null) continue;
								const delta = Math.abs(ts - target);
								if (delta < bestDelta) {
									bestDelta = delta;
									bestIdx = i;
								}
							}
							go(bestIdx);
						}}
					/>
				</div>

				{/* Events sidebar — live event timeline */}
				<EventsSidebar
					segments={segs}
					currentIndex={idx}
					onSelect={go}
					appColors={appColors}
					videoRef={vidRef}
				/>
			</div>

			{/* Bottom controls — single compact bar */}
			<div className="shrink-0 border-t bg-background">
				{/* Transport + speed + apps (single row, no duplication) */}
				<div className="flex items-center gap-0.5 px-3 py-1">
					{/* Playback controls */}
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						disabled={idx === 0}
						onClick={() => go(0)}
					>
						<SkipBack className="h-3 w-3" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						disabled={idx === 0}
						onClick={() => go(idx - 1)}
					>
						<ChevronLeft className="h-3 w-3" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						onClick={() => setPlaying((p) => !p)}
					>
						{playing ? (
							<Pause className="h-3 w-3" />
						) : (
							<Play className="h-3 w-3" />
						)}
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						disabled={idx === segs.length - 1}
						onClick={() => go(idx + 1)}
					>
						<ChevronRight className="h-3 w-3" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						disabled={idx === segs.length - 1}
						onClick={() => go(segs.length - 1)}
					>
						<SkipForward className="h-3 w-3" />
					</Button>
					<span className="text-[10px] tabular-nums text-muted-foreground ml-1">
						{idx + 1}/{segs.length}
					</span>

					<div className="h-3 border-l mx-1.5" />

					{/* Speed */}
					{SPEEDS.map((s) => (
						<button
							key={s}
							onClick={() => setSpeed(s)}
							className={`px-1 py-0.5 rounded text-[9px] font-mono ${speed === s ? "bg-foreground text-background font-bold" : "text-muted-foreground hover:text-foreground"}`}
						>
							{s}x
						</button>
					))}

					<div className="h-3 border-l mx-1.5" />

					{/* App jump — colored dots act as both legend AND jump buttons */}
					{Object.entries(appColors).map(([app, color]) => (
						<button
							key={app}
							onClick={() => {
								const i = segs.findIndex((s) => s.appName === app);
								if (i >= 0) go(i);
							}}
							className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground px-1 py-0.5 rounded hover:bg-muted transition"
							title={`→ ${app}`}
						>
							<span
								className="w-1.5 h-1.5 rounded-full shrink-0"
								style={{ backgroundColor: color }}
							/>
							{app}
						</button>
					))}

					<div className="flex-1" />

					{/* Keyboard shortcut hints */}
					<span className="text-[9px] text-muted-foreground/50">
						←→ · ␣ · O
					</span>
				</div>

				{/* Scrubber */}
				<div className="relative">
					<div className="flex h-4 mx-3 rounded overflow-hidden cursor-pointer border">
						{segs.map((seg, i) => {
							const color = seg.appName
								? appColors[seg.appName] || "hsl(var(--muted))"
								: "hsl(var(--muted))";
							return (
								<div
									key={seg.artifactId || i}
									className="relative hover:opacity-100 transition-opacity"
									style={{
										flex: 1,
										backgroundColor:
											i === idx
												? color
												: `color-mix(in srgb, ${color} 25%, transparent)`,
										opacity: i === idx ? 1 : 0.4,
									}}
									onClick={() => go(i)}
									onMouseEnter={() => setHover(i)}
									onMouseLeave={() => setHover(null)}
								>
									{i === idx && (
										<div className="absolute inset-0 ring-2 ring-white ring-inset rounded-sm" />
									)}
								</div>
							);
						})}
					</div>
					{hover != null && !!hoverSeg?.thumbnailUrl && (
						<div
							className="absolute bottom-full mb-2 pointer-events-none z-50"
							style={{
								left: `${((hover + 0.5) / segs.length) * 100}%`,
								transform: "translateX(-50%)",
							}}
						>
							<div className="rounded-lg overflow-hidden border border-white/20 shadow-xl bg-black">
								<img
									src={hoverSeg.thumbnailUrl as string}
									className="w-44 h-auto"
									alt=""
								/>
								<div className="px-2 py-0.5 text-[10px] text-white/80 bg-black/80">
									#{hover + 1} {(hoverSeg.appName as string) || ""}
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Filmstrip */}
				<Filmstrip
					segments={segs as { thumbnailUrl?: string }[]}
					currentIndex={idx}
					onSelect={go}
				/>
			</div>

			{/* OCR / Extracted text dialog */}
			<Dialog open={ocrOpen} onOpenChange={setOcrOpen}>
				<DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<FileText className="h-4 w-4" />
							Извлечённый текст — сегмент {idx + 1}
							{curAny?.isIndexed ? (
								<span className="text-xs text-green-600 flex items-center gap-1">
									<CheckCircle2 className="h-3 w-3" />
									Индексировано
								</span>
							) : (
								<span className="text-xs text-muted-foreground flex items-center gap-1">
									<Circle className="h-3 w-3" />
									Не индексировано
								</span>
							)}
						</DialogTitle>
					</DialogHeader>
					<div className="flex-1 overflow-y-auto space-y-4">
						{/* Context info */}
						{cur?.appName && (
							<div className="flex items-center gap-2 text-sm">
								<AppWindow className="h-4 w-4 text-muted-foreground" />
								<span className="font-medium">{cur.appName}</span>
								{cur.windowTitle && (
									<span className="text-muted-foreground">
										— {cur.windowTitle}
									</span>
								)}
							</div>
						)}
						{typeof curAny?.focusedUrl === "string" && curAny.focusedUrl && (
							<div className="text-xs text-blue-500 truncate">
								{curAny.focusedUrl}
							</div>
						)}

						{/* Visible apps */}
						{(() => {
							const visibleApps =
								(curAny?.visibleApps as string[] | undefined) ?? [];
							if (!visibleApps.length) return null;
							return (
								<div>
									<p className="text-xs font-medium text-muted-foreground mb-1">
										Видимые приложения
									</p>
									<div className="flex flex-wrap gap-1">
										{visibleApps.map((app, i) => (
											<span
												key={i}
												className="text-xs px-2 py-0.5 bg-muted rounded-full"
											>
												{app}
											</span>
										))}
									</div>
								</div>
							);
						})()}

						{/* OCR text (per-frame screen content) */}
						<div>
							<p className="text-xs font-medium text-muted-foreground mb-1">
								OCR — текст с экрана
							</p>
							<div className="bg-muted/50 rounded-md p-3 text-sm whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
								{(curAny?.ocrText as string) || (
									<span className="text-muted-foreground italic">
										OCR не выполнен
									</span>
								)}
							</div>
						</div>

						{/* Window context text */}
						<div>
							<p className="text-xs font-medium text-muted-foreground mb-1">
								Контекст окон
							</p>
							<div className="bg-muted/50 rounded-md p-3 text-xs font-mono whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto text-muted-foreground">
								{(curAny?.searchText as string) || (
									<span className="italic">—</span>
								)}
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
